'use client';

import React, { createContext, ReactElement, useContext, useEffect, useMemo, useState } from 'react';

import Image from 'next/image';

import infoIcon from '@/assets/icons/Tooltip-info-Icon.png';
import ImageIconShipping from '@/assets/icons/truck-icon-1.svg';
import ImageIconShippingThree from '@/assets/icons/truck-icon-3.svg';
import { SVGProps } from '@/components/svg/SVGProps';
import { useWindowDimensions } from '@/helpers/hooks';
import useEzShip from '@/helpers/hooks/useEzShip';
import cn from '@/helpers/utils/classNames';
import { DoneSharp as IconDone } from '@mui/icons-material';

import Tooltip from '../Tooltip';
import Paragraph from '../Typography/Paragraph';

type Place = 'any' | 'ezship-sidebar';

interface StepperContextProps {
  activeStep: number;
  totalSteps: number;
  flow?: 'before' | 'after';
  onStepChange?: (step: number) => void;
  place?: Place;
}

const StepperContext = createContext<StepperContextProps>({ activeStep: 0, totalSteps: 0, onStepChange: () => {} });

interface StepContextProps {
  stepNumber: number;
}

const StepContext = createContext<StepContextProps>({ stepNumber: 0 });

interface StepperProps {
  children: Array<ReactElement<typeof Step>>;
  className?: string;
  activeStep: number;
  flow?: 'before' | 'after';
  place?: Place;
  totalSteps?: number;
  isEzShip?: boolean;
}

const Stepper = ({ children, isEzShip, className, activeStep, flow, place, totalSteps }: StepperProps) => {
  const [currentActiveStep, setCurrentActiveStep] = useState<number>(0);
  const dimensions = useWindowDimensions();
  const handleStepChange = (step: number) => setCurrentActiveStep(step);

  const freeShippingMessage = useMemo(() => {
    switch (activeStep) {
      case 1:
        return 'Add 2 qualifying items on EZ-Ship for FREE SHIPPING!';
      case 2:
        return 'Add 1 qualifying item on EZ-Ship for FREE SHIPPING!';
      case 3:
        return 'FREE SHIPPING!';
      default:
        return 'Add 3 qualifying items on EZ-Ship for FREE SHIPPING!';
    }
  }, [activeStep]);

  useEffect(() => {
    if (activeStep <= children.length && activeStep >= 0) setCurrentActiveStep(activeStep);
  }, [activeStep, children.length]);

  const isMobileView = useMemo(() => {
    return !!(dimensions.width <= 768);
  }, [dimensions]);

  const isFreeShipping = activeStep === totalSteps;

  return (
    <StepperContext.Provider
      value={{
        activeStep: currentActiveStep,
        totalSteps: children.length,
        onStepChange: handleStepChange,
        flow,
        place,
      }}
    >
      <div className={className}>
        <div className={cn('v2-flex', { 'v2-justify-between': flow === 'after' })}>
          {children?.map((child, index) => {
            const { type: Child, props } = child;
            return (
              <StepContext.Provider key={index + 1} value={{ stepNumber: index + 1 }}>
                <Child stepNumber={index + 1} {...props} />
              </StepContext.Provider>
            );
          })}
        </div>
        <div className={cn('v2-flex', { 'v2-justify-end': isEzShip })}>
          {isFreeShipping && <IconDone className="v2-mr-1 v2-mt-2 v2-text-primary" fontSize="small" />}
          <p
            className={cn('v2-font-sweet-sans-pro-light v2-text-base v2-leading-[30px] text-neutral-800', {
              'v2-text-primary': isFreeShipping,
            })}
          >
            {freeShippingMessage}
            <Tooltip
              placement={isMobileView ? 'top' : 'left'}
              variant="light"
              wrapperClassName="!v2-inline-block"
              mainContainerClassName="v2-inline-block v2-top-[3px]"
              title={
                <div className="v2-py-3 v2-px-3">
                  <Paragraph
                    className="v2-mb-[8px] sm:v2-text-xs v2-font-sweet-sans-pro-medium  md:v2-font-sweet-sans-pro-medium md:v2-text-xs lg:v2-font-sweet-sans-pro-medium lg:v2-text-xs xs:v2-font-sweet-sans-pro-medium xs:v2-text-xs xxs:v2-font-sweet-sans-pro-medium xxs:v2-text-xs"
                    variant="gray"
                  >
                    Add 3 qualifying items to an EZSHIP to receive FREE shipping!
                  </Paragraph>
                  <div className="v2-mb-[8px] v2-flex v2-max-w-[12rem] v2-justify-start v2-items-center v2-gap-2">
                    <Image src={ImageIconShipping} className="v2-h-[18px] v2-w-[15px]" alt="Shipping One" />
                    <Paragraph
                      className="v2-text-left v2-font-normal sm:v2-text-xs md:v2-text-xs lg:v2-text-xs xs:v2-text-xs xxs:v2-text-xs"
                      variant="gray"
                    >
                      This is a qualifying item!
                    </Paragraph>
                  </div>
                  <div className="v2-mb-[8px] v2-flex v2-max-w-[12rem] v2-justify-start v2-items-center v2-gap-2">
                    <Image src={ImageIconShippingThree} className="v2-h-[18px] v2-w-[15px]" alt="Shipping Three" />
                    <Paragraph
                      className="v2-text-left v2-font-normal sm:v2-text-xs md:v2-text-xs lg:v2-text-xs xs:v2-text-xs xxs:v2-text-xs"
                      variant="gray"
                    >
                      This is an item that counts as 3 or more qualifying items!
                    </Paragraph>
                  </div>
                  <Paragraph
                    className="v2-font-sweet-sans-pro v2-font-normal sm:v2-text-xs md:v2-text-xs lg:v2-text-xs xs:v2-text-xs xxs:v2-text-xs"
                    variant="gray"
                  >
                    *Free shipping is a benefit available to all Paw Club members as long as they have 3 or more
                    Qualifying Items in their EZ Ship order. Qualifying Items include anything except kibble and canned
                    cat food. FREE SHIPPING Excludes Alaska, Hawaii and products purchased with pawPoints.
                  </Paragraph>
                </div>
              }
            >
              <Image src={infoIcon} width={20} height={20} alt="info" className="v2-cursor-pointer v2-ml-1 v2-mt-1" />
            </Tooltip>
          </p>
        </div>
      </div>
    </StepperContext.Provider>
  );
};

type StepIconCallback = (isStepActive: boolean) => ReactElement;

interface StepProps {
  icon: StepIconCallback | React.JSXElementConstructor<SVGProps> | ReactElement;
  className?: string;
  flow?: 'before' | 'after';
  lineColor?: string;
}

const isReactElement = (p: unknown): p is ReactElement => {
  return (p as ReactElement) !== undefined && (p as ReactElement).type !== undefined;
};

function isReactComponent(value: any): value is React.ComponentType<any> {
  return typeof value === 'function' && (value.prototype instanceof React.Component || 'isReactComponent' in value);
}

const Step = ({ icon: Icon, className, lineColor }: StepProps) => {
  const { activeStep, totalSteps, flow, place } = useContext(StepperContext);
  const { stepNumber } = useContext(StepContext);

  const isStepActive = activeStep >= stepNumber;

  const { ezShipId } = useEzShip();

  const inactiveClassName =
    !ezShipId || place === 'ezship-sidebar' ? 'before:v2-bg-primary-300' : 'before:v2-bg-slate-300';

  return (
    <div
      className={cn(
        "v2-flex v2-items-center v2-rounded-l-full v2-transition-all before:v2-h-[1px] before:v2-w-full before:v2-mx-1 before:v2-transition-all before:v2-content-[''] before:v2-duration-300",
        {
          'before:v2-bg-primary': isStepActive,
          [inactiveClassName]: !isStepActive,
          'first:before:v2-hidden': flow === 'after' && !(stepNumber === totalSteps),
          'v2-flex-grow': flow === 'before' || stepNumber !== 1,
          'before:v2-h-[1px]': flow === 'after',
          'before:!v2-mx-0': stepNumber === 1,
          'v2-w-[30px]': stepNumber === 2,
        },
        className
      )}
    >
      {isReactElement(Icon) ? (
        Icon
      ) : isReactComponent(Icon) ? (
        <Icon fill={isStepActive ? '#79C200' : 'black'} width="40" height="35" />
      ) : (
        (Icon as StepIconCallback)(isStepActive)
      )}
    </div>
  );
};

export const useStepper = (currentStep: number, totalSteps: number) => {
  const [activeStep, setActiveStep] = useState<number>(0);

  const isFirstStep = activeStep === 1;
  const isLastStep = activeStep === totalSteps;

  const onNextStep = () => {
    if (activeStep < totalSteps) setActiveStep(prev => prev + 1);
  };

  const onPreviousStep = () => {
    if (activeStep > 0) setActiveStep(prev => prev - 1);
  };

  const setCurrentActiveStep = (step: number) => {
    if (activeStep >= 0 && activeStep <= totalSteps) setActiveStep(step);
  };

  useEffect(() => {
    setActiveStep(currentStep);
  }, [currentStep]);

  return { activeStep, onNextStep, onPreviousStep, setCurrentActiveStep, isFirstStep, isLastStep, totalSteps };
};

Step.displayName = 'Step';
Stepper.Step = Step;

export default Stepper;
